import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getResponseData } from '../../../shared/utils/axios';
import { TermSheetCondition } from '../../../shared/models';

interface ConditionsDeleteDialogProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	condition: TermSheetCondition | null;
	close: () => void;
}

export const ConditionsDeleteDialog: React.FC<ConditionsDeleteDialogProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	condition,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (condition !== null) deleteCondition(condition);
	};

	const { mutate: deleteCondition } = useMutation<void, unknown, TermSheetCondition>(
		conditionToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.TermSheets.ConditionSpecific(
						dataRoomGuid,
						termSheetGuid,
						revisionGuid,
						conditionToDelete.guid
					)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Condition Deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the condition.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={condition !== null} onClose={close}>
			<DialogTitle>Confirm Delete Condition</DialogTitle>
			<DialogContent>
				<DialogContentText>Are you sure you want to delete?</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
